import {useRecoilState} from 'recoil';

import {ColumnElement, columnState} from './columnState';
import {ETableColumn} from './types';

export const getSelectionAndExpanderKeys = (): string[] => [
  '_selection',
  '_expander',
];

export const useColumnsConfig = ({
  userColumns,
  persistenceId,
}: {
  userColumns: ETableColumn<string, unknown>[];
  persistenceId?: string;
}): {
  initialHiddenColumns: string[];
  initialColumnOrder: string[];
  setColumnsStateByTableId: (
    state:
      | ((
          currentState: Record<string, ColumnElement[]>
        ) => Record<string, ColumnElement[]>)
      | Record<string, ColumnElement[]>
  ) => void;
  savedColumnConfig: ColumnElement[];
} => {
  const [columnsStateByTableId, setColumnsStateByTableId] =
    useRecoilState(columnState);

  function updateStoredColumConfig(columnConfig: ColumnElement[]) {
    if (persistenceId) {
      setColumnsStateByTableId((storedConfigs) => ({
        ...storedConfigs,
        [persistenceId]: columnConfig,
      }));
    }
  }

  function getStoredColumnConfig(): ColumnElement[] {
    const defaultColumnConfig = userColumns.map((column) => ({
      name: column.name,
      active: !column.defaultHidden,
      defaultHidden: Boolean(column.defaultHidden),
    }));

    if (!persistenceId) {
      return defaultColumnConfig;
    }

    const storedColumnConfig = columnsStateByTableId[persistenceId];
    if (!storedColumnConfig) {
      updateStoredColumConfig(defaultColumnConfig);
      return defaultColumnConfig;
    }

    const columnConfig: ColumnElement[] = [];

    // Only add columns from stored columns that are still available
    storedColumnConfig.forEach((column) => {
      const matchingDefaultColumnConfig = defaultColumnConfig.find(
        ({name, defaultHidden}) =>
          name === column.name && defaultHidden === column.defaultHidden
      );
      if (matchingDefaultColumnConfig) {
        columnConfig.push(column);
      }
    });

    let isModified = columnConfig.length !== storedColumnConfig.length;

    // Add newly added columns at its index
    defaultColumnConfig.forEach((column, index) => {
      if (!columnConfig.some(({name}) => name === column.name)) {
        columnConfig.splice(index, 0, column);
        isModified = true;
      }
    });

    if (isModified) {
      updateStoredColumConfig(columnConfig);
    }

    return columnConfig;
  }

  const storedColumnConfig = getStoredColumnConfig();

  const initialColumnOrder = [
    ...getSelectionAndExpanderKeys(),
    ...storedColumnConfig.map(({name}) => name),
  ];

  const initialHiddenColumns = storedColumnConfig
    .filter((column) => !column.active)
    .map((column) => column.name);

  return {
    initialHiddenColumns,
    initialColumnOrder,
    setColumnsStateByTableId,
    savedColumnConfig: storedColumnConfig,
  };
};
